import { useEffect, useRef } from 'react';

import { ChartDataRange } from '../../../../lib/store/market-data/charting/types';
import TradePriceCache from '../../../../lib/store-util/TradePriceCache';
import { CalculatedSymbolPrices } from '../../../../lib/util/ChartingHelpers';

import { useIsMobile } from '../../../../hooks';
import Button from '../../../../shared_components/Button/Button';
import { ButtonTypesEnum } from '../../../../shared_components/Button/Button.types';
import {
  chartHeaderElementRef,
  symbolDetailsScrollProgress,
} from '../../../../shared_components/utils/scroll/symbolDetailsScrollProgress';
import AddToWatchList from '../../../SymbolDetailsHeader/components/AddToWatchList';

import Company from './Components/Company/Company';
import Logo from './Components/Logo/Logo';
import MainPriceInfo from './Components/MainPriceInfo/MainPriceInfo';

import styles from './ChartHeader.module.scss';

type OwnProps = {
  symbol: string,
  currentRange: ChartDataRange,
  logo?: string,
  url?: string,
  company?: string,
  isSymbolInFavorites: boolean,
  handleCreateOrderSellOrBuy: (target: string, isHeaderBtn: boolean) => void;
  toggleFavoritesSubscription: () => void,
}
type Props = OwnProps;

/** Four layers fade in one after another across the scroll, deepening the blur. */
const BLUR_LAYER_STEP = 0.25;

const ChartHeader = (props: Props) => {
  const {
    logo,
    url,
    symbol,
    currentRange,
    company,
    isSymbolInFavorites,
    handleCreateOrderSellOrBuy,
    toggleFavoritesSubscription,
  } = props;
  const { isMobile } = useIsMobile();
  const { calculatedSymbolPrices } = TradePriceCache.use(symbol!, 'SymbolDetails/ChartHeader');
  const chartHeaderWrapperRef = useRef<HTMLDivElement>(null);
  const logoAndCompanyRef = useRef<HTMLDivElement>(null);
  const blurLayerRefs = useRef<(HTMLDivElement | null)[]>([null, null, null, null]);
  const rafId = useRef<number | null>(null);

  // Anchors the header's scroll progress: it measures this element to decide how far
  // the symbol has moved under the header.
  useEffect(() => {
    if (!isMobile) return undefined;
    chartHeaderElementRef.current = chartHeaderWrapperRef.current;

    return () => {
      chartHeaderElementRef.current = null;
      symbolDetailsScrollProgress.current = 0;
    };
  }, [isMobile]);

  useEffect(() => {
    if (!isMobile) return undefined;
    let prevProgress = -1;

    const tick = () => {
      const progress = symbolDetailsScrollProgress.current;
      if (progress !== prevProgress) {
        prevProgress = progress;
        blurLayerRefs.current.forEach((layer, index) => {
          if (!layer) return;
          const layerProgress = Math.min(Math.max((progress - index * BLUR_LAYER_STEP) / BLUR_LAYER_STEP, 0), 1);
          // eslint-disable-next-line no-param-reassign
          layer.style.opacity = String(layerProgress);
        });

        if (logoAndCompanyRef.current) {
          logoAndCompanyRef.current.style.opacity = String(1 - progress);
          logoAndCompanyRef.current.style.transform = `translateY(${-progress * 8}px)`;
        }
      }
      rafId.current = requestAnimationFrame(tick);
    };

    rafId.current = requestAnimationFrame(tick);

    return () => {
      if (rafId.current !== null) {
        cancelAnimationFrame(rafId.current);
        rafId.current = null;
      }
    };
  }, [isMobile]);

  const allData = {
    ...(calculatedSymbolPrices ?? {} as CalculatedSymbolPrices),
  };

  return (
    <>
      <div ref={chartHeaderWrapperRef}>
        <div
          ref={logoAndCompanyRef}
          className={isMobile ? styles.containerLogoAndCompanyMobile : styles.containerLogoAndCompany}
        >
          {isMobile && (
            <>
              <div ref={(el) => { blurLayerRefs.current[0] = el; }} className={styles.blurLayer1} />
              <div ref={(el) => { blurLayerRefs.current[1] = el; }} className={styles.blurLayer2} />
              <div ref={(el) => { blurLayerRefs.current[2] = el; }} className={styles.blurLayer3} />
              <div ref={(el) => { blurLayerRefs.current[3] = el; }} className={styles.blurLayer4} />
            </>
          )}
          <div className={styles.logoAndCompany}>
            <Logo logo={logo} url={url} symbol={symbol} animateOnScroll />
            <Company company={company} symbol={symbol} />
          </div>
          <Button
            variant={ButtonTypesEnum.Link}
            className={styles.addToWatchlist}
            onClick={() => toggleFavoritesSubscription()}
          >
            <AddToWatchList isAdded={isSymbolInFavorites} />
          </Button>
        </div>
        <MainPriceInfo
          {...allData}
          symbol={symbol}
          currentRange={currentRange}
          handleCreateOrderSellOrBuy={handleCreateOrderSellOrBuy}
        />
      </div>
    </>
  );
};

export default ChartHeader;
